import { config, getBackendSrv } from '@grafana/runtime';
import { lastValueFrom } from 'rxjs';
import { useState, useEffect } from 'react';
import { ASSISTANT_PLUGIN_ID } from '../plugin';
import { isVersionGreaterThan } from './versionCompare';
import type { components } from './settings.gen';

/**
 * The type of terms that apply to a tenant.
 * - 'msa': Master Service Agreement - only requires acceptance, no version checking
 * - 'termsAndConditions': Full terms and conditions - requires acceptance AND version checking
 */
export type TermsType = components['schemas']['TermsAndConditionsResponse']['termsType'];

/**
 * Internal result type for fetching terms data.
 */
interface TermsCheckResult {
  accepted: boolean;
  termsType: TermsType;
}

/**
 * Event name that is dispatched when terms and conditions are accepted.
 * Other components can listen to this event to refresh their UI.
 */
export const TERMS_AND_CONDITIONS_REFRESH_EVENT = 'grafana-assistant-terms-and-conditions-refresh';

/**
 * Internal function that fetches and evaluates terms data.
 * Returns both the acceptance status and the terms type.
 */
async function fetchTermsData(): Promise<TermsCheckResult> {
  const backendSrv = getBackendSrv();
  const appSubUrl = config?.appSubUrl ?? '';
  const apiUrl = `${appSubUrl}/api/plugins/${ASSISTANT_PLUGIN_ID}/resources/api/v1/settings/terms`;

  const response = await lastValueFrom(
    backendSrv.fetch({
      url: apiUrl,
      method: 'GET',
      showErrorAlert: false,
    })
  );

  // The API returns { status: "success", data: { ... } }, so we need to access response.data.data
  const responseBody = response.data as { data: components['schemas']['TermsAndConditionsResponse'] };
  const termsData = responseBody.data;

  const termsAccepted = termsData.acceptedTermsAndConditions ?? false;
  const acceptedVersion = termsData.acceptedVersion;
  const currentVersion = termsData.version;
  const termsType = termsData.termsType;

  // For MSA, we only check if terms are accepted
  if (termsType === 'msa') {
    return { accepted: termsAccepted, termsType };
  }

  // Check if terms are not accepted, or if no version was recorded or current version is missing
  if (!termsAccepted || !acceptedVersion || !currentVersion) {
    return { accepted: false, termsType };
  }

  // Check if the current version is newer than the accepted version
  const needsAcceptance = isVersionGreaterThan(currentVersion, acceptedVersion);
  return { accepted: termsAccepted && !needsAcceptance, termsType };
}

/**
 * Checks whether terms and conditions are accepted and up-to-date.
 * This function verifies that:
 * 1. Terms have been accepted
 * 2. The accepted version matches or exceeds the current version
 *
 * @returns A promise that resolves to `true` if terms are accepted and up-to-date, `false` otherwise
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * import { checkTerms } from '@grafana/assistant';
 *
 * const accepted = await checkTerms();
 * if (!accepted) {
 *   // Show terms acceptance UI
 *   console.log('Terms need to be accepted');
 * }
 * ```
 */
export async function checkTerms(): Promise<boolean> {
  try {
    const result = await fetchTermsData();
    return result.accepted;
  } catch (error) {
    // If API call fails, throw the error so callers can handle it
    throw new Error(`Failed to check terms requirements: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * React hook to check terms and conditions acceptance status.
 * Automatically fetches terms data on mount and re-fetches when terms change
 * (via TERMS_AND_CONDITIONS_REFRESH_EVENT).
 *
 * @returns An object containing whether terms are accepted, the terms type, loading state, and error state
 *
 * @example
 * ```typescript
 * import { useTerms } from '@grafana/assistant';
 *
 * function MyComponent() {
 *   const { accepted, termsType, loading, error } = useTerms();
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   return <div>Terms accepted</div>;
 * }
 * ```
 */
export function useTerms() {
  const [accepted, setAccepted] = useState<boolean>(false);
  const [termsType, setTermsType] = useState<TermsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTerms() {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchTermsData();
        setAccepted(result.accepted);
        setTermsType(result.termsType);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to check terms';
        setError(errorMessage);
        setAccepted(false);
        setTermsType(null);
      } finally {
        setLoading(false);
      }
    }

    fetchTerms();

    // Listen for the refresh event to re-fetch when terms change
    const handleTermsRefresh = () => {
      fetchTerms();
    };
    document.addEventListener(TERMS_AND_CONDITIONS_REFRESH_EVENT, handleTermsRefresh);
    return () => {
      document.removeEventListener(TERMS_AND_CONDITIONS_REFRESH_EVENT, handleTermsRefresh);
    };
  }, []);

  return {
    accepted,
    termsType,
    loading,
    error,
  };
}
